import React, { Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Card, CardBody, CardHeader, Col, Container, Row, Label, Input, Spinner } from 'reactstrap';
import BreadCrumb from '../components/Common/BreadCrumb';
import Flatpickr from "react-flatpickr";
import dateFormat from "dateformat";
import moment from "moment";
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';

import { createDeepClone } from "../utils/index";

import CardLoader from "../components/CardLoader";
import ActionList from "../components/ActionList";
import ServiceDocumentGrid from "../components/ServiceDocumentGrid";

import { useDispatch, useSelector } from "react-redux";
import * as technicalReviewerActions from "../actions/TechnicalReviewerActions";

const TechnicalReviewerEditScreen  = (props) => {
    const { register, clearErrors, reset, trigger, formState: { errors } } = useForm({mode: 'all'});

    const navigate = useNavigate();
    const { id } = useParams();

    const dispatch = useDispatch();

    const loading = useSelector((state) => state.technicalReviewer.loadingFBO);
    const fbo = useSelector((state) => state.technicalReviewer.fbo);
    
    const [fboState, setFBOState] = React.useState(fbo);
    const [isDirty, setIsDirty] = React.useState(false);
    const [refreshAction, setRefreshAction] = React.useState(false);
    const [selectedStandard, setSelectedStandard] = React.useState([]);

    React.useEffect(() => {
	    dispatch(technicalReviewerActions.loadTechnicalReviewer(id, navigate));
    }, [id]);

    React.useEffect(() => {
		setFBOState(fbo);
        setIsDirty(false);
        setRefreshAction(!refreshAction);
        setSelectedStandard([]);
        
        if(fbo) {
            reset(fbo.technicalReviewer);

            if(fbo.technicalReviewerStandardList != null) {
                setSelectedStandard(fbo.technicalReviewerStandardList);
            }
        }
        
        clearErrors();
    }, [fbo]);

    const reloadAction = () => {
        setRefreshAction(!refreshAction);
    }

    const onChangeDocument = (isProduct) => {
        setRefreshAction(!refreshAction);
    }

    const getFormattedDate = (value) => {
        if(value) {
            return dateFormat(value, "mm/dd/yyyy");
        }
        return "";
    }

    const getFormattedDateTime = (value) => {
        if(value) {
            return dateFormat(value, "mm/dd/yyyy HH:MM");
        }
        return "";
    }

    const getDateWithoutTimezone = (value) => {
        if(value) {
            const tzoffset = value.getTimezoneOffset() * 60000;
            const withoutTimezone = new Date(value.valueOf() - tzoffset).toISOString().slice(0, -1);
            return withoutTimezone;
        }
        return "";
    }

    const onTechnicalReviewerChange = (name, value) => {
        var copy_fboState = createDeepClone(fboState);
        
        if(value) {
            copy_fboState.technicalReviewer[name] = value;
        }
        else if(value === false) {
            copy_fboState.technicalReviewer[name] = false;
        }
        else {
            copy_fboState.technicalReviewer[name] = null;
        }
        
        setFBOState(copy_fboState);
        setIsDirty(true);
    }

    const onStandardChange = (items) => {
        setSelectedStandard(items);
        
        if(items.length > 0) {
            var ids = [];
            
            items.forEach(element => {
                ids.push(element.id);
            });

            onTechnicalReviewerChange("standardId", ids.toString());
        }
        else {
            onTechnicalReviewerChange("standardId", null);
        }
    }

    const onSaveTechnicalReviewer = async() => {
        const valid = await trigger();
        if(valid) {
            dispatch(technicalReviewerActions.saveTechnicalReviewer(fboState, navigate));
        }
    }

    const onCancelTechnicalReviewer = () => {
        setFBOState(fbo);

        if(fbo.technicalReviewerStandardList != null) {
            setSelectedStandard(fbo.technicalReviewerStandardList);
        }
        else {
            setSelectedStandard([]);
        }

        setIsDirty(false);
        clearErrors();
    }
    
    return(
        <Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={"Edit Technical Reviewer"} />
                    <Row>
                        <Col lg={fboState && fboState.technicalReviewer.id > 0 ? 9 : 12}>
                            <CardLoader loading={loading} />

                            <Card>
                                <CardHeader className="border-0 d-flex align-items-center">
                                    <h5 className="card-title mb-0 flex-grow-1">Technical Reviewer</h5>
                                    <div className="flex-shrink-0">
                                        <div className="d-flex flex-wrap gap-2">
                                            <button className="btn btn-primary btn-label"
                                                disabled={loading || !isDirty}
                                                onClick={onSaveTechnicalReviewer}
                                            >
                                                <i className="ri-save-line label-icon align-middle"></i> Save Technical Reviewer
                                            </button>
                                            <button className="btn btn-danger btn-label"
                                                disabled={loading || !isDirty}
                                                onClick={onCancelTechnicalReviewer}
                                            >
                                                <i className="ri-close-line label-icon align-middle"></i> Cancel
                                            </button>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardBody className="border border-dashed border-end-0 border-start-0">
                                    <Row className="gy-2">
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">First Name</Label>
                                            <input type="text" className="form-control" readOnly={true}
                                                value={(fboState && fboState.userProfile.firstName) || ""}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Last Name</Label>
                                            <input type="text" className="form-control" readOnly={true}
                                                value={(fboState && fboState.userProfile.lastName) || ""}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Email</Label>
                                            <input type="text" className="form-control" readOnly={true}
                                                value={(fboState && fboState.userProfile.email) || ""}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Employee ID</Label>
                                            <input type="text" className="form-control" readOnly={true}
                                                value={(fboState && fboState.userProfile.employeeId) || ""}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Telephone Number</Label>
                                            <input type="text" className="form-control" readOnly={true}
                                                value={(fboState && fboState.userProfile.telephoneNumber) || ""}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Mobile Number</Label>
                                            <input type="text" className="form-control" readOnly={true}
                                                value={(fboState && fboState.userProfile.mobileNumber) || ""}
                                            />
                                        </Col>
                                        <Col xxl={12} md={12}>
                                            <Label className="form-label">Man Day Standard <span className="text-primary" style={{fontSize:12}}><i>(Technical Reviewers would be allowed to assign tickets based on selected standards)</i></span></Label>
                                            <Typeahead
                                                id="fldStandardIds"
                                                multiple
                                                disabled={false}
                                                labelKey= {option => `${option.standardName}`}
                                                options={fboState && fboState.standardList || []}
                                                placeholder="Select Standard..."
                                                selected = {selectedStandard}
                                                onChange = {(item) => onStandardChange(item)}
                                            />
                                        </Col>
                                        <Col xxl={8} md={8}>
                                            <Label className="form-label">Address</Label>
                                            <input type="text" maxLength={256} className="form-control"
                                                value={(fboState && fboState.technicalReviewer.address) || ""}
                                                onChange={e => onTechnicalReviewerChange("address", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">City</Label>
                                            <input type="text" maxLength={256} className="form-control"
                                                value={(fboState && fboState.technicalReviewer.city) || ""}
                                                onChange={e => onTechnicalReviewerChange("city", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">State</Label>
                                            <input type="text" maxLength={256} className="form-control"
                                                value={(fboState && fboState.technicalReviewer.state) || ""}
                                                onChange={e => onTechnicalReviewerChange("state", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Country</Label>
                                            <input type="text" maxLength={256} className="form-control"
                                                value={(fboState && fboState.technicalReviewer.country) || ""}
                                                onChange={e => onTechnicalReviewerChange("country", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Zip Code</Label>
                                            <input type="text" maxLength={256} className="form-control"
                                                value={(fboState && fboState.technicalReviewer.zipCode) || ""}
                                                onChange={e => onTechnicalReviewerChange("zipCode", e.target.value)}
                                            />
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                            {fboState && fboState.technicalReviewer.id > 0 &&
                                <ServiceDocumentGrid
                                    serviceId={-4}
                                    refId={fboState.technicalReviewer.id}
                                    refName={"TECHNICAL_REVIEWER"}
                                    parentId={fboState.technicalReviewer.id}
                                    parentName={"TECHNICAL_REVIEWER"}
                                    isProduct={false}
                                    collapsed={"false"}
                                    canAddDocument={true}
                                    canDeleteDocument={true}
                                    canAddCertificate={false}
                                    canDeleteCertificate={false}
                                    onChange={onChangeDocument}
                                />
                            }
                        </Col>
                        
                        {fboState && fboState.technicalReviewer.id > 0 &&
                        <Col lg={3}>
                            <ActionList id={fboState.technicalReviewer.id} name={"TECHNICAL_REVIEWER"} refreshAction={refreshAction} />
                        </Col>
                        }
                    </Row>
                </Container>
            </div>
        </Fragment>
    );
}

export default TechnicalReviewerEditScreen;